/**
 * NotificationDropdown Component
 * Displays notifications in a dropdown from the navigation bell icon
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  X,
  CheckCheck,
  Info,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Megaphone,
  ArrowLeft,
  ExternalLink,
} from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import { Notification, NotificationType } from '../services/notificationService';

interface NotificationDropdownProps {
  className?: string;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    notifications,
    unreadCount,
    isLoading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    dismiss,
  } = useNotifications();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedNotification(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch full list when dropdown opens
  const handleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      fetchNotifications(true); // Include read notifications
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }
    setSelectedNotification(notification);
  };

  const handleActionClick = (notification: Notification) => {
    if (notification.actionUrl) {
      setIsOpen(false);
      setSelectedNotification(null);

      // Check if internal route, static file, or external URL
      if (notification.actionUrl.startsWith('/')) {
        // Static files (like .html) need a full page load, not React Router
        if (notification.actionUrl.includes('.')) {
          window.location.href = notification.actionUrl;
        } else {
          navigate(notification.actionUrl);
        }
      } else {
        window.open(notification.actionUrl, '_blank');
      }
    }
  };

  const handleDismiss = async (notification: Notification) => {
    await dismiss(notification.id);
    setSelectedNotification(null);
  };

  const getTypeIcon = (type: NotificationType) => {
    switch (type) {
      case 'Warning':
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'Error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'Success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'Announcement':
        return <Megaphone className="w-4 h-4 text-purple-500" />;
      default:
        return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Bell Button with Badge */}
      <button
        onClick={handleOpen}
        className="p-2 rounded-lg hover:bg-black/5 transition-colors relative"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell className="w-5 h-5 text-[#64748b]" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#c41e3a] text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white/95 backdrop-blur-xl rounded-xl border border-black/[0.08] shadow-xl overflow-hidden z-50 animate-slide-down">
          {/* Header */}
          <div className="px-4 py-3 border-b border-black/[0.06] bg-gradient-to-br from-[#f8fafc] to-white flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[#0f172a]">Notifications</h3>
            {unreadCount > 0 && !selectedNotification && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-[#c41e3a] hover:text-[#a01830] flex items-center space-x-1 transition-colors"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                <span>Mark all read</span>
              </button>
            )}
          </div>

          {/* Notification List or Detail View */}
          <div className="max-h-96 overflow-y-auto">
            {selectedNotification ? (
              // Detail View
              <div className="p-4">
                <button
                  onClick={() => setSelectedNotification(null)}
                  className="text-xs text-[#64748b] hover:text-[#0f172a] flex items-center space-x-1 mb-3 transition-colors"
                >
                  <ArrowLeft className="w-3 h-3" />
                  <span>Back to list</span>
                </button>
                <div className="flex items-start space-x-3">
                  {getTypeIcon(selectedNotification.type)}
                  <div className="flex-1">
                    <h4 className="font-semibold text-[#0f172a] text-sm">
                      {selectedNotification.title}
                    </h4>
                    <p className="text-xs text-[#64748b] mt-0.5">
                      {formatDate(selectedNotification.createdAt)}
                    </p>
                    <p className="text-sm text-[#475569] mt-2 whitespace-pre-wrap">
                      {selectedNotification.message}
                    </p>

                    {selectedNotification.actionUrl && (
                      <button
                        onClick={() => handleActionClick(selectedNotification)}
                        className="mt-3 px-4 py-2 bg-[#c41e3a] text-white text-sm font-medium rounded-lg hover:bg-[#a01830] transition-colors flex items-center space-x-2"
                      >
                        <span>{selectedNotification.actionLabel || 'View Details'}</span>
                        {!selectedNotification.actionUrl.startsWith('/') && (
                          <ExternalLink className="w-3 h-3" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDismiss(selectedNotification)}
                  className="w-full mt-4 py-2 text-xs text-[#64748b] hover:text-[#dc2626] transition-colors border-t border-black/[0.06] flex items-center justify-center space-x-1"
                >
                  <X className="w-3 h-3" />
                  <span>Dismiss this notification</span>
                </button>
              </div>
            ) : isLoading ? (
              <div className="p-8 text-center text-[#64748b]">
                <div className="animate-spin w-6 h-6 border-2 border-[#c41e3a] border-t-transparent rounded-full mx-auto mb-2"></div>
                <p className="text-sm">Loading...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-[#64748b]">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              // List View
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`px-4 py-3 border-b border-black/[0.04] hover:bg-black/[0.02] cursor-pointer transition-colors ${
                    !notification.isRead ? 'bg-blue-50/50' : ''
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    {getTypeIcon(notification.type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4
                          className={`text-sm truncate ${
                            !notification.isRead
                              ? 'font-semibold text-[#0f172a]'
                              : 'font-medium text-[#475569]'
                          }`}
                        >
                          {notification.title}
                        </h4>
                        {!notification.isRead && (
                          <span className="w-2 h-2 bg-[#c41e3a] rounded-full flex-shrink-0 ml-2" />
                        )}
                      </div>
                      <p className="text-xs text-[#64748b] truncate mt-0.5">
                        {notification.message}
                      </p>
                      <p className="text-xs text-[#94a3b8] mt-1">
                        {formatDate(notification.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && !selectedNotification && (
            <div className="px-4 py-2 border-t border-black/[0.06] bg-[#f8fafc]">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/notifications');
                }}
                className="text-xs text-[#c41e3a] hover:text-[#a01830] font-medium transition-colors"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
